import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send, Video, Phone, Image as ImageIcon, X, Mic, Square, Pause, Play, MoreVertical, Ban, Trophy, Gamepad2 } from 'lucide-react';
import PingPongGame from '../components/PingPongGame';
import QuizGame from '../components/QuizGame';
import PhotoMatchWidget, { PhotoMatchChatWidget, parsePhotoMatchMessage, isPhotoMatchMessage } from '../components/PhotoMatchWidget';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import VoiceNotePlayer from '../components/VoiceNotePlayer';
import EmojiPicker from '../components/EmojiPicker';
import TwilioVideoCall from '../components/TwilioVideoCall';
import BlockButton from '../components/BlockButton';

// Wrapper component to load high scores
const PingPongGameWithScores = ({ matchId, currentUser, otherUser, onClose, loadMessages, challengerPhotos = [], opponentPhotos = [] }) => {
  const [highScores, setHighScores] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    const loadHighScores = async () => {
      try {
        const response = await axios.get(`${API}/ping-pong/high-scores/${matchId}`, {
          withCredentials: true
        });
        setHighScores(response.data);
      } catch (error) {
        console.error('Failed to load high scores:', error);
        setHighScores({ user_high_score: 0, opponent_high_score: 0, scores: [] });
      } finally {
        setLoading(false);
      }
    };
    loadHighScores();
  }, [matchId, API]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8">
          <p className="text-gray-600">Loading game...</p>
        </div>
      </div>
    );
  }

  return (
    <PingPongGame
      onClose={onClose}
      onShareScore={(score, opponentName, userHigh, opponentHigh, photosToShare) => {
        // Send score with both sets of photos
        // Format: [PING_PONG_CHALLENGE:score:playerName:userAPhotos|userBPhotos]
        const userAStr = photosToShare?.userAPhotos?.join(',') || '';
        const userBStr = photosToShare?.userBPhotos?.join(',') || '';
        const photosStr = (userAStr || userBStr) ? `:${userAStr}|${userBStr}` : '';
        const scoreMessage = `[PING_PONG_CHALLENGE:${score}:${currentUser.name}${photosStr}]`;
        
        axios.post(
          `${API}/matches/${matchId}/messages`,
          { text: scoreMessage },
          { withCredentials: true }
        ).then(() => {
          loadMessages();
        }).catch((err) => {
          console.error('Failed to share score:', err);
        });
      }}
      playerName={currentUser.name}
      opponentName={otherUser.name}
      matchId={matchId}
      userHighScore={highScores?.user_high_score || 0}
      opponentHighScore={highScores?.opponent_high_score || 0}
      challengerPhotos={challengerPhotos}
      opponentPhotos={opponentPhotos}
    />
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Utility functions for link detection and embedding
const extractYouTubeId = (url) => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

const extractLinks = (text) => {
  if (!text) return [];
  const urlPattern = /(https?:\/\/[^\s]+)/g;
  return text.match(urlPattern) || [];
};

const ChatLinkPreview = ({ url, isMe }) => {
  const youtubeId = extractYouTubeId(url);
  
  if (youtubeId) {
    return (
      <div className="mt-2 rounded-xl overflow-hidden shadow-lg bg-black max-w-sm">
        <iframe
          width="100%"
          height="200"
          src={`https://www.youtube.com/embed/${youtubeId}`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full"
        ></iframe>
      </div>
    );
  }
  
  // For other links, show a clickable preview
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`mt-2 block p-3 border rounded-xl transition-all max-w-sm ${
        isMe 
          ? 'bg-white/20 border-white/30 hover:bg-white/30' 
          : 'bg-gray-100 border-gray-200 hover:bg-gray-200'
      }`}
    >
      <div className="flex items-center space-x-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          isMe ? 'bg-white/30' : 'bg-blue-100'
        }`}>
          <span className={isMe ? 'text-white text-xs' : 'text-blue-600 text-xs'}>🔗</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-semibold truncate ${isMe ? 'text-white' : 'text-blue-600'}`}>
            {url}
          </p>
          <p className={`text-xs ${isMe ? 'text-white/70' : 'text-gray-500'}`}>
            Tap to open link
          </p>
        </div>
      </div>
    </a>
  );
};

// Game info for invites
const GAME_INFO = {
  'get-to-know-you': { name: 'Get to Know You', emoji: '💬', type: 'GAME_INVITE_GTK' },
  'love-psychology-quiz': { name: 'Love & Psychology Quiz', emoji: '💕', type: 'GAME_INVITE_LOVE' },
  'compatibility-challenge': { name: 'Compatibility Challenge', emoji: '🧠', type: 'GAME_INVITE_COMPAT' },
  'ping-pong': { name: 'Ping Pong Game', emoji: '🏓', type: 'PING_PONG_CHALLENGE' }
};

const Chat = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [gameInviteSent, setGameInviteSent] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [otherUser, setOtherUser] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [showPingPongGame, setShowPingPongGame] = useState(false);
  const [challengerPhotos, setChallengerPhotos] = useState([]); // User A's photos
  const [opponentPhotos, setOpponentPhotos] = useState([]); // User B's photos
  const [emojiPosition, setEmojiPosition] = useState({ x: 0, y: 0 });
  const [showQuizGame, setShowQuizGame] = useState(false);
  const [activeQuizGameId, setActiveQuizGameId] = useState(null);
  const [quizExistingAnswers, setQuizExistingAnswers] = useState(null);
  
  // Video call state
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [callType, setCallType] = useState('video'); // 'video' or 'audio'
  const [match, setMatch] = useState(null); // Store match data for video calls
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const [showPhotoMatchModal, setShowPhotoMatchModal] = useState(false);
  const [photoMatchMode, setPhotoMatchMode] = useState(null); // 'upload' or 'results'

  useEffect(() => {
    loadData();
    const interval = setInterval(loadMessages, 3000); // Poll every 3 seconds
    return () => clearInterval(interval);
  }, [matchId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle game invite from URL parameter
  useEffect(() => {
    const gameParam = searchParams.get('game');
    
    if (gameParam && currentUser && otherUser && !loading && !gameInviteSent) {
      const gameData = GAME_INFO[gameParam];
      if (gameData) {
        // Mark as sent to prevent duplicate sends
        setGameInviteSent(true);
        
        // For ping pong, open the game directly
        if (gameParam === 'ping-pong') {
          setShowPingPongGame(true);
        } else {
          // For other games, send an invite widget message
          sendGameInvite(gameParam, gameData);
        }
        
        // Clear the URL parameter
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, currentUser, otherUser, loading, gameInviteSent, setSearchParams]);

  const sendGameInvite = async (gameId, gameData) => {
    try {
      console.log('Sending game invite:', gameId, gameData);
      const inviteMessage = `[GAME_INVITE:${JSON.stringify({
        gameId,
        gameName: gameData.name,
        emoji: gameData.emoji,
        inviterName: currentUser?.name || 'Someone'
      })}]`;
      
      const response = await axios.post(
        `${API}/matches/${matchId}/messages`,
        { text: inviteMessage },
        { withCredentials: true }
      );
      
      console.log('Game invite sent successfully:', response.data);
      toast.success(`${gameData.emoji} ${gameData.name} invite sent!`);
      loadMessages();
    } catch (error) {
      console.error('Failed to send game invite:', error);
      toast.error('Failed to send game invite');
    }
  };

  const loadData = async () => {
    try {
      const [userRes, messagesRes, matchesRes] = await Promise.all([
        axios.get(`${API}/auth/me`),
        axios.get(`${API}/matches/${matchId}/messages`),
        axios.get(`${API}/matches`)
      ]);
      
      setCurrentUser(userRes.data);
      setMessages(messagesRes.data);
      
      // Find the match to get other user info
      const matchData = matchesRes.data.find(m => m.match_id === matchId);
      if (matchData) {
        setOtherUser(matchData.user);
        setMatch(matchData); // Store match data for video calls
      }
      
      setLoading(false);
    } catch (e) {
      console.error('Failed to load chat:', e);
      toast.error('Failed to load chat');
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const response = await axios.get(`${API}/matches/${matchId}/messages`);
      setMessages(response.data);
    } catch (e) {
      // Silently fail for polling
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !selectedImage) || sending) return;

    setSending(true);
    try {
      await axios.post(`${API}/matches/${matchId}/messages`, {
        text: newMessage,
        image_url: selectedImage
      });
      setNewMessage('');
      setSelectedImage(null);
      await loadMessages();
    } catch (e) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      // Cloudinary returns full URL, use it directly
      const imageUrl = response.data.url;
      setSelectedImage(imageUrl);
      toast.success('Image uploaded!');
    } catch (e) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const startRecording = async () => {
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Voice recording is not supported in this browser');
        return;
      }

      // Check if we're on HTTPS (required for microphone access)
      if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
        toast.error('Voice recording requires a secure connection (HTTPS)');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      toast.success('Recording started');
    } catch (e) {
      console.error('Recording error:', e);
      
      // Provide specific error messages based on error type
      if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
        toast.error(
          <div>
            <p className="font-semibold">Microphone access denied</p>
            <p className="text-xs mt-1">Please enable microphone permissions in your browser settings</p>
          </div>,
          { duration: 5000 }
        );
      } else if (e.name === 'NotFoundError' || e.name === 'DevicesNotFoundError') {
        toast.error('No microphone found. Please connect a microphone and try again.');
      } else if (e.name === 'NotReadableError' || e.name === 'TrackStartError') {
        toast.error('Microphone is already in use by another application');
      } else if (e.name === 'NotSupportedError') {
        toast.error('Voice recording is not supported on this device');
      } else if (e.name === 'SecurityError') {
        toast.error(
          <div>
            <p className="font-semibold">Security Error</p>
            <p className="text-xs mt-1">Voice recording requires HTTPS or localhost</p>
          </div>,
          { duration: 5000 }
        );
      } else {
        toast.error('Failed to start recording. Please check your microphone permissions.');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setRecordingTime(0);
    audioChunksRef.current = [];
  };

  const sendVoiceNote = async () => {
    if (!audioBlob) return;

    setUploadingAudio(true);
    try {
      // Upload audio file
      const formData = new FormData();
      formData.append('file', audioBlob, 'voice-note.webm');

      const uploadResponse = await axios.post(`${API}/upload/audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      // Cloudinary returns full URL, use it directly
      const audioUrl = uploadResponse.data.url;

      // Send message with audio
      await axios.post(`${API}/matches/${matchId}/messages`, {
        text: '',
        audio_url: audioUrl
      });

      // Reset state
      setAudioBlob(null);
      setRecordingTime(0);
      await loadMessages();
      toast.success('Voice note sent!');
    } catch (e) {
      console.error('Send voice note error:', e);
      toast.error('Failed to send voice note');
    } finally {
      setUploadingAudio(false);
    }
  };

  // Long press handlers for emoji reactions
  const handleTouchStart = (e, messageId) => {
    e.preventDefault(); // Prevent text selection on long press
    const touch = e.touches[0];
    setEmojiPosition({ x: touch.clientX, y: touch.clientY });
    setSelectedMessageId(messageId);
    
    longPressTimerRef.current = setTimeout(() => {
      setShowEmojiPicker(true);
      // Haptic feedback on mobile if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, 500); // Show after 500ms hold
  };

  const handleTouchEnd = (e) => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }
  };

  const handleTouchMove = () => {
    // Cancel long press if user moves finger
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }
  };

  const handleMouseDown = (e, messageId) => {
    setEmojiPosition({ x: e.clientX, y: e.clientY });
    setSelectedMessageId(messageId);
    
    longPressTimerRef.current = setTimeout(() => {
      setShowEmojiPicker(true);
    }, 500);
  };

  const handleMouseUp = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }
  };

  const handleEmojiSelect = async (emoji) => {
    try {
      await axios.post(
        `${API}/matches/${matchId}/messages/${selectedMessageId}/react`,
        { emoji },
        { withCredentials: true }
      );
      await loadMessages();
      setShowEmojiPicker(false);
      setSelectedMessageId(null);
    } catch (e) {
      toast.error('Failed to add reaction');
    }
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (showEmojiPicker) {
        setShowEmojiPicker(false);
        setSelectedMessageId(null);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showEmojiPicker]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-400 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      {/* Header */}
      <div className="bg-white/90 backdrop-blur-sm shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center space-x-4">
          <Button
            data-testid="back-to-matches-btn"
            onClick={() => navigate('/matches')}
            variant="ghost"
            size="icon"
            className="hover:bg-pink-100 rounded-full"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </Button>
          
          {otherUser && (
            <div className="flex items-center space-x-3 flex-1">
              <Avatar className="w-10 h-10">
                <AvatarImage src={otherUser.photos?.[0]?.startsWith('http') ? otherUser.photos[0] : (otherUser.photos?.[0] ? `${BACKEND_URL}${otherUser.photos[0]}` : otherUser.picture)} />
                <AvatarFallback className="bg-gradient-to-br from-pink-400 to-purple-500 text-white">
                  {otherUser.name[0]}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h2 className="font-bold text-gray-800">{otherUser.name}</h2>
                <p className="text-xs text-gray-500">Active now</p>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  data-testid="video-call-btn"
                  variant="ghost"
                  size="icon"
                  className="hover:bg-pink-100 rounded-full"
                  onClick={() => {
                    if (match && match.user) {
                      setCallType('video');
                      setShowVideoCall(true);
                    }
                  }}
                  disabled={!match || !match.user}
                >
                  <Video className="w-5 h-5 text-gray-700" />
                </Button>
                <Button
                  data-testid="voice-call-btn"
                  variant="ghost"
                  size="icon"
                  className="hover:bg-pink-100 rounded-full"
                  onClick={() => {
                    if (match && match.user) {
                      setCallType('audio');
                      setShowVideoCall(true);
                    }
                  }}
                  disabled={!match || !match.user}
                >
                  <Phone className="w-5 h-5 text-gray-700" />
                </Button>
                
                {/* Block Button */}
                {otherUser && (
                  <BlockButton 
                    userId={otherUser.id}
                    userName={otherUser.name}
                    isBlocked={otherUser.i_blocked_them}
                    onBlockChange={() => navigate('/matches')}
                    size="icon"
                    variant="ghost"
                    showIcon={true}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6 max-w-4xl mx-auto w-full">
        <div className="space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-pink-400" />
              </div>
              <p className="text-gray-500">No messages yet. Say hi!</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isMe = msg.sender_id === currentUser?.id;
              return (
                <div
                  key={msg.id}
                  data-testid={`message-${msg.id}`}
                  className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[70%] ${isMe ? 'order-2' : 'order-1'} relative`}>
                    <div
                      className={`rounded-2xl px-4 py-2.5 ${
                        isMe
                          ? 'bg-gradient-to-br from-pink-500 to-purple-600 text-white rounded-br-sm'
                          : 'bg-white/90 text-gray-800 rounded-bl-sm'
                      } shadow-sm select-none`}
                      onTouchStart={(e) => handleTouchStart(e, msg.id)}
                      onTouchEnd={handleTouchEnd}
                      onTouchMove={handleTouchMove}
                      onMouseDown={(e) => handleMouseDown(e, msg.id)}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp}
                    >
                      {msg.image_url && (
                        <img
                          src={msg.image_url.startsWith('http') ? msg.image_url : `${BACKEND_URL}${msg.image_url}`}
                          alt="Shared"
                          className="rounded-lg mb-2 max-w-full cursor-pointer hover:opacity-90"
                          onClick={() => window.open(msg.image_url.startsWith('http') ? msg.image_url : `${BACKEND_URL}${msg.image_url}`, '_blank')}
                        />
                      )}
                      {msg.audio_url && (
                        <VoiceNotePlayer 
                          audioUrl={msg.audio_url.startsWith('http') ? msg.audio_url : `${BACKEND_URL}${msg.audio_url}`}
                          isMe={isMe}
                        />
                      )}
                      {msg.text && (
                        <>
                          {/* Check if it's a game invite message */}
                          {msg.text.trim().startsWith('[GAME_INVITE:') && msg.text.trim().endsWith(']') ? (
                            (() => {
                              try {
                                const jsonStr = msg.text.trim().slice(13, -1);
                                const data = JSON.parse(jsonStr);
                                return (
                                  <div className="my-2">
                                    <div className={`rounded-xl p-4 shadow-lg max-w-xs ${
                                      isMe 
                                        ? 'bg-gradient-to-br from-purple-100 to-pink-100' 
                                        : 'bg-gradient-to-br from-blue-50 to-purple-50'
                                    }`}>
                                      <div className="flex items-center justify-center mb-3">
                                        <div className="text-4xl">{data.emoji} 🎮</div>
                                      </div>
                                      <p className="text-center text-gray-700 text-sm font-semibold mb-2">
                                        {isMe ? 'You invited them to play!' : `${data.inviterName} invites you to play!`}
                                      </p>
                                      <div className="bg-white rounded-lg py-3 px-4 mb-3 text-center">
                                        <span className="text-lg font-bold text-purple-600">{data.gameName}</span>
                                      </div>
                                      {!isMe && (
                                        <Button
                                          onClick={() => {
                                            if (data.gameId === 'ping-pong') {
                                              setShowPingPongGame(true);
                                            } else {
                                              // Open quiz game
                                              setActiveQuizGameId(data.gameId);
                                              setQuizExistingAnswers(null);
                                              setShowQuizGame(true);
                                            }
                                          }}
                                          className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold"
                                        >
                                          🎮 Accept & Play!
                                        </Button>
                                      )}
                                      {isMe && (
                                        <p className="text-center text-xs text-gray-500">
                                          Waiting for them to accept...
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                );
                              } catch (e) {
                                return <p className="break-words whitespace-pre-wrap">{msg.text}</p>;
                              }
                            })()
                          ) : msg.text.trim().startsWith('[QUIZ_ANSWERS:') && msg.text.trim().endsWith(']') ? (
                            (() => {
                              try {
                                const jsonStr = msg.text.trim().slice(14, -1);
                                const data = JSON.parse(jsonStr);
                                return (
                                  <div className="my-2">
                                    <div className={`rounded-xl p-4 shadow-lg max-w-xs ${
                                      isMe ? 'bg-gradient-to-br from-blue-100 to-cyan-100' : 'bg-gradient-to-br from-purple-50 to-pink-50'
                                    }`}>
                                      <div className="flex items-center justify-center mb-3">
                                        <div className="text-4xl">{data.emoji} ✨</div>
                                      </div>
                                      <p className="text-center text-gray-700 text-sm font-semibold mb-2">
                                        {isMe ? 'You completed the quiz!' : `${data.playerName} completed the quiz!`}
                                      </p>
                                      <div className="bg-white rounded-lg py-2 px-4 mb-3 text-center">
                                        <span className="text-md font-bold text-purple-600">{data.gameName}</span>
                                      </div>
                                      {!isMe && (
                                        <Button
                                          onClick={() => {
                                            setActiveQuizGameId(data.gameId);
                                            setQuizExistingAnswers(data.answers);
                                            setShowQuizGame(true);
                                          }}
                                          className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold"
                                        >
                                          📝 Answer & Compare!
                                        </Button>
                                      )}
                                      {isMe && (
                                        <p className="text-center text-xs text-gray-500">Waiting for their answers...</p>
                                      )}
                                    </div>
                                  </div>
                                );
                              } catch (e) {
                                return <p className="break-words whitespace-pre-wrap">{msg.text}</p>;
                              }
                            })()
                          ) : msg.text.trim().startsWith('[QUIZ_RESULT:') && msg.text.trim().endsWith(']') ? (
                            (() => {
                              try {
                                const jsonStr = msg.text.trim().slice(13, -1);
                                const data = JSON.parse(jsonStr);
                                return (
                                  <div className="my-2">
                                    <div className="rounded-xl p-4 shadow-lg max-w-xs bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-200">
                                      <div className="flex items-center justify-center mb-2">
                                        <div className="text-4xl">🎉 {data.emoji}</div>
                                      </div>
                                      <p className="text-center text-gray-700 text-sm font-semibold mb-2">
                                        {data.gameName} Results
                                      </p>
                                      <div className="bg-white rounded-lg py-3 px-4 mb-2 text-center">
                                        <div className="text-4xl font-bold text-purple-600">{data.score}%</div>
                                        <p className="text-sm text-gray-600">Compatibility</p>
                                      </div>
                                      <p className="text-center text-xs text-gray-500">
                                        {data.playerA} & {data.playerB}
                                      </p>
                                    </div>
                                  </div>
                                );
                              } catch (e) {
                                return <p className="break-words whitespace-pre-wrap">{msg.text}</p>;
                              }
                            })()
                          ) : parsePhotoMatchMessage(msg.text) ? (
                            <PhotoMatchChatWidget
                              message={msg}
                              isMe={isMe}
                              currentUser={currentUser}
                              otherUser={otherUser}
                              matchId={matchId}
                              onOpenPhotoMatch={(mode) => {
                                setPhotoMatchMode(mode);
                                setShowPhotoMatchModal(true);
                              }}
                            />
                          ) : msg.text.trim().startsWith('[PING_PONG_CHALLENGE:') && msg.text.trim().endsWith(']') ? (
                            (() => {
                              // Parse challenge format: [PING_PONG_CHALLENGE:score:playerName:userAPhotos|userBPhotos]
                              const trimmedText = msg.text.trim();
                              const content = trimmedText.slice(21, -1); // Remove [PING_PONG_CHALLENGE: and ]
                              
                              // Use regex to properly parse - avoid splitting URLs by ':'
                              // Format: score:playerName:photosString (where photosString contains URLs with colons)
                              const firstColonIdx = content.indexOf(':');
                              const secondColonIdx = content.indexOf(':', firstColonIdx + 1);
                              
                              const score = content.slice(0, firstColonIdx);
                              const playerName = secondColonIdx > -1 
                                ? content.slice(firstColonIdx + 1, secondColonIdx)
                                : content.slice(firstColonIdx + 1);
                              const photosString = secondColonIdx > -1 ? content.slice(secondColonIdx + 1) : '';
                              
                              // Photos are in photosString as "userAPhotos|userBPhotos"
                              let userAPhotos = [];
                              let userBPhotos = [];
                              if (photosString) {
                                const pipeIdx = photosString.indexOf('|');
                                const userAStr = pipeIdx > -1 ? photosString.slice(0, pipeIdx) : photosString;
                                const userBStr = pipeIdx > -1 ? photosString.slice(pipeIdx + 1) : '';
                                userAPhotos = userAStr ? userAStr.split(',').filter(p => p.startsWith('http')) : [];
                                userBPhotos = userBStr ? userBStr.split(',').filter(p => p.startsWith('http')) : [];
                              }
                              const hasPhotos = userAPhotos.length > 0 || userBPhotos.length > 0;
                              const hasBothPhotos = userAPhotos.length > 0 && userBPhotos.length > 0;
                              const allPhotos = [...userAPhotos, ...userBPhotos];
                              
                              return (
                                  <div className="my-2">
                                    <div className={`rounded-xl p-4 shadow-lg ${
                                      isMe 
                                        ? 'bg-gradient-to-br from-purple-100 to-pink-100' 
                                        : 'bg-gradient-to-br from-blue-50 to-purple-50'
                                    }`}>
                                      <div className="flex items-center justify-center mb-3">
                                        <div className="text-4xl">🏓</div>
                                      </div>
                                      <p className="text-center text-gray-700 text-sm mb-2 font-semibold">
                                        {isMe ? 'You challenged them!' : `${playerName} challenges you!`}
                                      </p>
                                      <p className="text-center text-gray-600 text-xs mb-2">
                                        See if you can beat {isMe ? 'your' : 'their'} score
                                      </p>
                                      <div className="bg-white rounded-lg py-3 px-4 mb-3 text-center">
                                        <div className="flex items-center justify-center space-x-2">
                                          <Trophy className="w-5 h-5 text-yellow-500" />
                                          <span className="text-3xl font-bold text-purple-600">{score}</span>
                                          <span className="text-gray-600 text-sm">points</span>
                                        </div>
                                      </div>
                                      {/* Show photo preview if available */}
                                      {hasPhotos && (
                                        <div className="mb-3">
                                          <p className="text-center text-purple-500 text-xs mb-2">
                                            {hasBothPhotos 
                                              ? `📸 ${allPhotos.length} photos (alternating both players!)`
                                              : `📸 ${allPhotos.length} photo${allPhotos.length > 1 ? 's' : ''} to view!`}
                                          </p>
                                          <div className="flex justify-center gap-1">
                                            {allPhotos.slice(0, 4).map((url, idx) => (
                                              <img 
                                                key={idx} 
                                                src={url} 
                                                alt="" 
                                                className={`w-10 h-10 rounded-lg object-cover border-2 shadow ${
                                                  idx < userAPhotos.length ? 'border-purple-400' : 'border-pink-400'
                                                }`}
                                              />
                                            ))}
                                            {allPhotos.length > 4 && (
                                              <div className="w-10 h-10 rounded-lg bg-purple-200 flex items-center justify-center text-purple-700 text-xs font-bold">
                                                +{allPhotos.length - 4}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      {!isMe && (
                                        <>
                                          <Button
                                            onClick={() => {
                                              setChallengerPhotos(userAPhotos);
                                              setOpponentPhotos(userBPhotos);
                                              setShowPingPongGame(true);
                                            }}
                                            className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold"
                                          >
                                            🎮 {hasBothPhotos ? 'Continue Challenge!' : 'Play Now!'}
                                          </Button>
                                        </>
                                      )}
                                      {isMe && (
                                        <p className="text-center text-xs text-gray-500">
                                          Waiting for them to accept your challenge...
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                );
                            })()
                          ) : isPhotoMatchMessage(msg.text) ? (
                            // Fallback for photo match messages that failed to parse - show simple widget
                            <div className="my-2">
                              <div className="rounded-xl p-4 shadow-lg bg-gradient-to-br from-pink-50 to-purple-50">
                                <div className="flex items-center justify-center mb-2">
                                  <div className="text-3xl">📸💕</div>
                                </div>
                                <p className="text-center text-gray-700 text-sm">Photo Match Request</p>
                                <button
                                  onClick={() => {
                                    setPhotoMatchMode('upload');
                                    setShowPhotoMatchModal(true);
                                  }}
                                  className="w-full mt-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white py-2 px-4 rounded-lg text-sm"
                                >
                                  Open Photo Match
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p 
                              className={`break-words whitespace-pre-wrap ${
                                /[\p{Emoji}\u200d]+/gu.test(msg.text)
                                  ? 'cursor-pointer hover:bg-white/20 rounded p-1 -m-1 transition-colors' 
                                  : ''
                              }`}
                              onClick={() => {
                                // Check if message contains emojis
                                const emojiRegex = /[\p{Emoji}\u200d]+/gu;
                                const isEmojiMessage = emojiRegex.test(msg.text);
                                
                                if (isEmojiMessage) {
                                  setShowPingPongGame(true);
                                }
                              }}
                            >
                              {msg.text}
                            </p>
                          )}
                          {/* Render embedded content for links */}
                          {!msg.text.trim().startsWith('[PING_PONG_CHALLENGE:') && !msg.text.trim().startsWith('[GAME_INVITE:') && !msg.text.trim().startsWith('[QUIZ_ANSWERS:') && !msg.text.trim().startsWith('[QUIZ_RESULT:') && !isPhotoMatchMessage(msg.text) && extractLinks(msg.text).map((link, idx) => (
                            <ChatLinkPreview key={idx} url={link} isMe={isMe} />
                          ))}
                        </>
                      )}
                    </div>
                    
                    {/* Reactions */}
                    {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                      <div className={`flex flex-wrap gap-1 mt-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
                        {Object.entries(msg.reactions).reduce((acc, [userId, emoji]) => {
                          // Group same emojis together
                          const existingGroup = acc.find(g => g.emoji === emoji);
                          if (existingGroup) {
                            existingGroup.count++;
                            existingGroup.users.push(userId);
                          } else {
                            acc.push({ emoji, count: 1, users: [userId] });
                          }
                          return acc;
                        }, []).map((group, idx) => (
                          <div
                            key={idx}
                            className="flex items-center space-x-1 bg-white/90 rounded-full px-2 py-1 shadow-sm border border-gray-200"
                          >
                            <span className="text-sm">{group.emoji}</span>
                            {group.count > 1 && (
                              <span className="text-xs font-semibold text-gray-600">{group.count}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <p className={`text-xs text-gray-400 mt-1 ${isMe ? 'text-right' : 'text-left'}`}>
                      {msg.created_at && formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Emoji Picker */}
      {showEmojiPicker && (
        <EmojiPicker onSelect={handleEmojiSelect} position={emojiPosition} />
      )}

      {/* Input */}
      <div className="bg-white/90 backdrop-blur-sm border-t border-gray-200 px-4 py-4">
        {otherUser?.is_blocked ? (
          <div className="max-w-4xl mx-auto px-4 py-6">
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 text-center">
              <Ban className="w-8 h-8 text-red-600 mx-auto mb-2" />
              <p className="text-red-800 font-semibold">Unable to send messages</p>
              <p className="text-sm text-red-600 mt-1">
                {otherUser.i_blocked_them 
                  ? 'You have blocked this user'
                  : 'This user is unavailable for messaging'}
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSend} className="max-w-4xl mx-auto">
          {/* Image Preview */}
          {selectedImage && (
            <div className="mb-3 relative inline-block">
              <img
                src={selectedImage}
                alt="Preview"
                className="w-24 h-24 object-cover rounded-lg"
              />
              <button
                type="button"
                onClick={() => setSelectedImage(null)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Voice Note Preview */}
          {audioBlob && !isRecording && (
            <div className="mb-3 bg-purple-50 border-2 border-purple-300 rounded-lg p-3">
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-purple-700">Voice Note Ready</p>
                  <p className="text-xs text-purple-600">{Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}</p>
                </div>
                <Button
                  type="button"
                  onClick={cancelRecording}
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={sendVoiceNote}
                  disabled={uploadingAudio}
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {uploadingAudio ? 'Sending...' : 'Send'}
                </Button>
              </div>
            </div>
          )}

          {/* Recording Indicator */}
          {isRecording && (
            <div className="mb-3 bg-red-50 border-2 border-red-300 rounded-lg p-3">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse"></div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-700">Recording...</p>
                  <p className="text-xs text-red-600">{Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}</p>
                </div>
                <Button
                  type="button"
                  onClick={stopRecording}
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <Square className="w-4 h-4 mr-1" />
                  Stop
                </Button>
              </div>
            </div>
          )}
          
          <div className="flex items-center space-x-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage || selectedImage || isRecording || audioBlob}
              variant="ghost"
              size="icon"
              className="rounded-full hover:bg-blue-100"
            >
              <ImageIcon className="w-5 h-5 text-blue-500" />
            </Button>
            <Button
              type="button"
              onClick={isRecording ? stopRecording : startRecording}
              disabled={uploadingImage || selectedImage || audioBlob}
              variant="ghost"
              size="icon"
              className={`rounded-full ${isRecording ? 'bg-red-100 hover:bg-red-200' : 'hover:bg-purple-100'}`}
            >
              {isRecording ? (
                <Square className="w-5 h-5 text-red-600" />
              ) : (
                <Mic className="w-5 h-5 text-purple-600" />
              )}
            </Button>
            {/* Photo Match Widget */}
            {otherUser && currentUser && (
              <PhotoMatchWidget
                matchId={matchId}
                currentUser={currentUser}
                otherUser={otherUser}
                onRefreshMessages={loadMessages}
                externalOpen={showPhotoMatchModal}
                externalMode={photoMatchMode}
                onExternalClose={() => {
                  setShowPhotoMatchModal(false);
                  setPhotoMatchMode(null);
                }}
              />
            )}
            {/* Ping Pong Game Invite Button */}
            <Button
              type="button"
              onClick={() => setShowPingPongGame(true)}
              variant="ghost"
              size="icon"
              className="rounded-full hover:bg-green-100"
              title="Play Ping Pong Game"
            >
              <Gamepad2 className="w-5 h-5 text-green-600" />
            </Button>
            <Input
              data-testid="message-input"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={selectedImage ? "Add a caption..." : "Type a message..."}
              className="flex-1 rounded-full bg-gray-100 border-none focus:ring-2 focus:ring-pink-400"
            />
            <Button
              type="submit"
              data-testid="send-message-btn"
              disabled={(!newMessage.trim() && !selectedImage) || sending}
              size="icon"
              className="rounded-full bg-gradient-to-br from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 w-12 h-12"
            >
              <Send className="w-5 h-5 text-white" />
            </Button>
          </div>
        </form>
        )}
      </div>

      {/* Video Call Component */}
      {showVideoCall && match && match.user && (
        <TwilioVideoCall
          matchId={matchId}
          matchName={match.user.name || 'User'}
          matchPhoto={
            match.user.photos?.[0] && !match.user.photos[0].includes('datematch-app')
              ? (match.user.photos[0].startsWith('http') ? match.user.photos[0] : `${BACKEND_URL}${match.user.photos[0]}`)
              : match.user.picture || ''
          }
          callType={callType}
          onClose={() => setShowVideoCall(false)}
        />
      )}

      {/* Ping Pong Game */}
      {showPingPongGame && otherUser && currentUser && (
        <PingPongGameWithScores
          matchId={matchId}
          currentUser={currentUser}
          otherUser={otherUser}
          onClose={() => {
            setShowPingPongGame(false);
            setChallengerPhotos([]);
            setOpponentPhotos([]);
          }}
          loadMessages={loadMessages}
          challengerPhotos={challengerPhotos}
          opponentPhotos={opponentPhotos}
        />
      )}

      {/* Quiz Game */}
      {showQuizGame && activeQuizGameId && otherUser && currentUser && (
        <QuizGame
          gameId={activeQuizGameId}
          matchId={matchId}
          currentUser={currentUser}
          otherUser={otherUser}
          existingAnswers={quizExistingAnswers}
          onClose={() => {
            setShowQuizGame(false);
            setActiveQuizGameId(null);
            setQuizExistingAnswers(null);
          }}
          onComplete={() => {
            loadMessages();
          }}
        />
      )}
    </div>
  );
};

export default Chat;
